import { useEffect, useState } from 'react'
import { useToast } from '../../context/ToastContext'

export default function CostCalculatorSection() {
  const [duration, setDuration] = useState(300)
  const [cost, setCost] = useState(null)
  const { showToast } = useToast()

  // Segments workflow: Combined AI analysis at $0.025/sec
  const SEGMENTS_COST_PER_SECOND = 0.025

  useEffect(() => {
    calculateCost()
  }, [duration])

  const calculateCost = () => {
    const totalCost = duration * SEGMENTS_COST_PER_SECOND
    setCost(parseFloat(totalCost.toFixed(3)))
  }

  const formatDuration = () => {
    const minutes = Math.floor(duration / 60)
    const seconds = duration % 60
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
  }

  return (
    <section id="pricing-calc" className="py-16 bg-white dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Real-Time Cost Calculator</h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            See exactly what you'll pay - by the second, not by the minute
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8 shadow-lg">
          {/* Duration Slider */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Video Duration: <span className="font-bold text-lg text-primary-600">{formatDuration()}</span>
            </label>
            <input
              type="range"
              min="1"
              max="3600"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
              <span>1 second</span>
              <span>1 hour</span>
            </div>
          </div>

          {/* Workflow Description */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">What You Get</h3>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-4 space-y-3">
              <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                <div className="w-3 h-3 bg-primary-600 rounded-full mr-3 mt-1"></div>
                <div className="flex-1">
                  <span className="font-medium">Visual Analysis</span>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Face detection, pose tracking, object recognition, scene classification
                  </div>
                </div>
              </div>

              <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                <div className="w-3 h-3 bg-secondary-600 rounded-full mr-3 mt-1"></div>
                <div className="flex-1">
                  <span className="font-medium">Semantic Understanding</span>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    AI-powered content analysis, shot detection, motion tracking
                  </div>
                </div>
              </div>

              <div className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                <div className="w-3 h-3 bg-tertiary-600 rounded-full mr-3 mt-1"></div>
                <div className="flex-1">
                  <span className="font-medium">Editor-Ready Output</span>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    EDL, FCPXML, and JSON formats for Final Cut Pro, Premiere, and more
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-300 dark:border-gray-600 my-3"></div>

              <div className="flex items-center font-semibold text-gray-900 dark:text-white">
                <span>Complete Analysis</span>
                <span className="ml-auto text-primary-600">${SEGMENTS_COST_PER_SECOND.toFixed(3)}/sec</span>
              </div>
            </div>
          </div>

          {/* Cost Result */}
          <div className="bg-white dark:bg-gray-700 rounded-lg p-8 text-center border-2 border-primary-200 dark:border-primary-800">
            {cost !== null ? (
              <>
                <div className="text-5xl font-bold text-green-600 dark:text-green-400 mb-3">${cost.toFixed(3)}</div>
                <div className="text-lg text-gray-600 dark:text-gray-400 mb-2">
                  for {formatDuration()} of processing
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500 mb-3">
                  Complete video analysis with unified editor-friendly moments
                </div>
                <div className="text-xs text-green-600 dark:text-green-400 mt-3">
                  ✓ Includes EDL, FCPXML, and JSON output formats
                </div>
                <div className="text-xs text-green-600 dark:text-green-400">
                  ✓ All analysis runs in parallel - lightning fast!
                </div>
              </>
            ) : (
              <div className="text-gray-500 dark:text-gray-400">Calculating...</div>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 rounded">
            <div className="flex items-start">
              <i className="fas fa-info-circle text-blue-600 dark:text-blue-400 mr-3 mt-0.5"></i>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Moments Workflow:</strong> Our intelligent AI-powered workflow analyzes your video content and delivers unified moments optimized for video editing software like Final Cut Pro and Adobe Premiere.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
